import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import selfHealingService from '../services/SelfHealingService';

const HealthIndicator = () => {
  const [healthStatus, setHealthStatus] = useState(selfHealingService.healthStatus);
  const [showDetails, setShowDetails] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    // Subscribe to health status changes
    const unsubscribe = selfHealingService.subscribe((status) => {
      setHealthStatus(status);
      // If recovering from unhealthy, show reconnecting state
      if (status.api === 'healthy' && status.network === 'healthy') {
        setIsReconnecting(false);
      }
    });

    // Listen for reconnection events
    const handleReconnect = () => {
      setIsReconnecting(true);
      setTimeout(() => setIsReconnecting(false), 3000);
    };
    window.addEventListener('wsReconnect', handleReconnect);

    return () => {
      unsubscribe();
      window.removeEventListener('wsReconnect', handleReconnect);
    };
  }, []);

  const isHealthy = healthStatus.api === 'healthy' && healthStatus.network === 'healthy';
  const isOffline = healthStatus.network === 'offline';
  const isDegraded = healthStatus.api === 'degraded' || healthStatus.api === 'unhealthy';

  // Don't show indicator when everything is healthy and not recovering
  if (isHealthy && !isReconnecting && healthStatus.lastCheck) return null;

  const getStatusColor = () => {
    if (isOffline) return 'bg-red-500';
    if (isDegraded) return 'bg-yellow-500';
    if (isReconnecting) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStatusIcon = () => {
    if (isOffline) return <WifiOff className="w-4 h-4" />;
    if (isDegraded) return <AlertCircle className="w-4 h-4" />;
    if (isReconnecting) return <RefreshCw className="w-4 h-4 animate-spin" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (isOffline) return 'You are offline';
    if (isDegraded) return 'Connection issues';
    if (isReconnecting) return 'Reconnecting...';
    return 'Connected';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-50"
        data-testid="health-indicator"
      >
        <div 
          className={`${getStatusColor()} px-4 py-2 cursor-pointer`}
          onClick={() => setShowDetails(!showDetails)}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-white text-sm">
            {getStatusIcon()}
            <span>{getStatusText()}</span>
            {(isOffline || isDegraded) && (
              <span className="text-white/80 text-xs ml-2">
                (Auto-recovery active)
              </span>
            )}
          </div>
        </div>

        {/* Detailed Status Panel */}
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-gray-900 text-white overflow-hidden"
            >
              <div className="max-w-4xl mx-auto p-4">
                <h4 className="font-medium mb-3">System Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${healthStatus.network === 'healthy' ? 'bg-green-400' : 'bg-red-400'}`} />
                    <span>Network: {healthStatus.network || 'checking...'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${healthStatus.api === 'healthy' ? 'bg-green-400' : healthStatus.api === 'degraded' ? 'bg-yellow-400' : 'bg-red-400'}`} />
                    <span>API: {healthStatus.api || 'checking...'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${healthStatus.storage === 'healthy' ? 'bg-green-400' : 'bg-red-400'}`} />
                    <span>Storage: {healthStatus.storage || 'checking...'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${healthStatus.websocket === 'connected' ? 'bg-green-400' : 'bg-yellow-400'}`} />
                    <span>WebSocket: {healthStatus.websocket || 'checking...'}</span>
                  </div>
                </div>
                {healthStatus.lastCheck && (
                  <p className="text-xs text-gray-400 mt-3">
                    Last check: {new Date(healthStatus.lastCheck).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default HealthIndicator;
